'use client'

import Image from 'next/image'
import { useMemo } from 'react'
import { Annotation, Annotation2 } from '@/types/annotation'

// --------------

interface SignpostingPlace {
  name: string
  address: string
  phone?: string
  rating?: number
  reviews?: number
  hours?: string
  isOpen?: boolean
  type?: string
  thumbnail?: string
  website?: string
  mapsUrl?: string
  latitude?: number
  longitude?: number
}

// --------------

interface SignpostingResult {
  places: SignpostingPlace[]
  query: string
  location: { latitude: number; longitude: number }
}

// --------------

function buildMapsUrl(place: SignpostingPlace): string {
  if (place.mapsUrl) return place.mapsUrl
  const query = encodeURIComponent(`${place.name} ${place.address}`)
  return `https://www.google.com/maps/search/?api=1&query=${query}`
}

// --------------

/**
 * Calculate distance between two coordinates (km)
 */
function calculateDistance(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 6371
  const dLat = (lat2 - lat1) * (Math.PI / 180)
  const dLng = (lng2 - lng1) * (Math.PI / 180)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// --------------

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)}m`
  return `${km.toFixed(1)}km`
}

// --------------

type OpenStatus = 'open' | 'closed' | 'closing-soon' | null

/**
 * Determine open/closed/closing-soon from hours string + current time.
 * Handles formats like:
 * - "Open ⋅ Closes 9 PM"
 * - "Closed ⋅ Opens 8 AM Mon"
 * - "Monday: 9 AM–9 PM"
 * - "Open 24 hours"
 */
function getOpenStatus(
  hoursStr: string | undefined,
  apiIsOpen: boolean | undefined
): { status: OpenStatus; label: string } {
  // If API provides the value directly, use it
  if (apiIsOpen === true) return { status: 'open', label: 'Open' }
  if (apiIsOpen === false) return { status: 'closed', label: 'Closed' }

  if (!hoursStr) return { status: null, label: '' }

  const lower = hoursStr.toLowerCase()

  // Check for explicit open/closed in the string
  if (lower.includes('open 24 hours') || lower.includes('open 24h')) {
    return { status: 'open', label: 'Open 24h' }
  }

  if (lower.startsWith('open')) {
    // "Open ⋅ Closes 9 PM" - check if closing soon
    const closesMatch = lower.match(/closes?\s+(\d{1,2})\s*(am|pm)/i)
    if (closesMatch) {
      const hour = parseInt(closesMatch[1])
      const isPm = closesMatch[2].toLowerCase() === 'pm'
      const closesAt = isPm && hour !== 12 ? hour + 12 : hour
      const now = new Date()
      const currentHour = now.getHours()
      const diff = closesAt - currentHour
      if (diff > 0 && diff <= 1) {
        return { status: 'closing-soon', label: `Closes at ${closesMatch[1]} ${closesMatch[2].toUpperCase()}` }
      }
      return { status: 'open', label: `Closes ${closesMatch[1]} ${closesMatch[2].toUpperCase()}` }
    }
    return { status: 'open', label: 'Open' }
  }

  if (lower.startsWith('closed')) {
    const opensMatch = lower.match(/opens?\s+(\d{1,2})\s*(am|pm)/i)
    if (opensMatch) {
      return { status: 'closed', label: `Opens ${opensMatch[1]} ${opensMatch[2].toUpperCase()}` }
    }
    return { status: 'closed', label: 'Closed' }
  }

  // Try to parse "Day: TIME–TIME" format against current time
  const now = new Date()
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const today = days[now.getDay()]

  if (lower.includes(today)) {
    const timeMatch = hoursStr.match(/(\d{1,2})\s*(AM|PM)\s*[–-]\s*(\d{1,2})\s*(AM|PM)/i)
    if (timeMatch) {
      let openHour = parseInt(timeMatch[1])
      const openAmPm = timeMatch[2].toLowerCase()
      let closeHour = parseInt(timeMatch[3])
      const closeAmPm = timeMatch[4].toLowerCase()

      if (openAmPm === 'pm' && openHour !== 12) openHour += 12
      if (openAmPm === 'am' && openHour === 12) openHour = 0
      if (closeAmPm === 'pm' && closeHour !== 12) closeHour += 12
      if (closeAmPm === 'am' && closeHour === 12) closeHour = 0

      const currentHour = now.getHours()
      if (currentHour >= openHour && currentHour < closeHour) {
        const diff = closeHour - currentHour
        if (diff <= 1) {
          return { status: 'closing-soon', label: `Closes at ${timeMatch[3]} ${timeMatch[4]}` }
        }
        return { status: 'open', label: `Closes ${timeMatch[3]} ${timeMatch[4]}` }
      }
      return { status: 'closed', label: `Opens ${timeMatch[1]} ${timeMatch[2]}` }
    }
  }

  return { status: null, label: '' }
}

// --------------

function PlaceCard({
  place,
  userLocation
}: {
  place: SignpostingPlace
  userLocation?: { latitude: number; longitude: number }
}) {
  const mapsUrl = buildMapsUrl(place)

  const distance =
    userLocation && place.latitude && place.longitude
      ? calculateDistance(
          userLocation.latitude, userLocation.longitude,
          place.latitude, place.longitude
        )
      : undefined

  const { status, label } = useMemo(
    () => getOpenStatus(place.hours, place.isOpen),
    [place.hours, place.isOpen]
  )

  return (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`View ${place.name} on Google Maps`}
      className="border-border/40 bg-card/50 hover:border-primary/30 group flex w-64 shrink-0 flex-col overflow-hidden rounded-xl border backdrop-blur-sm transition-all hover:shadow-md"
    >
      {/* Thumbnail - only if real photo available */}
      {place.thumbnail && (
        <div className="relative h-28 w-full overflow-hidden">
          <Image
            src={place.thumbnail}
            alt={`Photo of ${place.name}`}
            fill
            className="object-cover"
            sizes="256px"
            unoptimized
          />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col gap-1 p-3">
        {/* Name + status */}
        <div className="flex items-start justify-between gap-2">
          <p className="text-foreground text-sm font-semibold leading-tight">
            {place.name}
          </p>
          {status && (
            <span
              className={`mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                status === 'open'
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                  : status === 'closing-soon'
                    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                    : 'bg-red-500/15 text-red-600 dark:text-red-400'
              }`}
            >
              {status === 'open' ? 'Open' : status === 'closing-soon' ? 'Closing soon' : 'Closed'}
            </span>
          )}
        </div>

        {/* Type / category */}
        {place.type && (
          <p className="text-muted-foreground text-[11px]">
            {place.type}
          </p>
        )}

        {/* Address */}
        {place.address && (
          <p className="text-muted-foreground text-xs leading-snug">
            {place.address}
          </p>
        )}

        {/* Status detail + Distance + Rating */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {label && (
            <span className="text-muted-foreground text-[11px]">
              {label}
            </span>
          )}
          {distance !== undefined && (
            <span className="text-muted-foreground text-[11px]">
              📍 {formatDistance(distance)}
            </span>
          )}
          {place.rating !== undefined && (
            <span className="flex items-center gap-0.5">
              <span className="text-[11px] text-amber-500">★</span>
              <span className="text-muted-foreground text-[11px]">
                {place.rating.toFixed(1)}
                {place.reviews !== undefined && ` (${place.reviews})`}
              </span>
            </span>
          )}
        </div>

        {/* Phone */}
        {place.phone && (
          <p className="text-muted-foreground text-[11px]">
            📞 {place.phone}
          </p>
        )}
      </div>
    </a>
  )
}

// --------------

export const SignpostingPlacesAnnotation: React.FC<{
  annotation: Annotation | Annotation2
}> = ({ annotation }) => {
  const result = (annotation as Record<string, unknown>)['signposting__searchNearbyServices'] as
    Record<string, unknown> | undefined
  if (!result) return null

  const data: SignpostingResult =
    ('result' in result ? result.result : result) as SignpostingResult

  if (!data?.places?.length) return null

  // Sort by distance (closest first)
  const sortedPlaces = data.location
    ? [...data.places].sort((a, b) => {
        const distA = a.latitude && a.longitude
          ? calculateDistance(data.location.latitude, data.location.longitude, a.latitude, a.longitude)
          : Infinity
        const distB = b.latitude && b.longitude
          ? calculateDistance(data.location.latitude, data.location.longitude, b.latitude, b.longitude)
          : Infinity
        return distA - distB
      })
    : data.places

  return (
    <div className="mt-3 space-y-2">
      {/* Header */}
      <div>
        <p className="text-foreground/80 text-sm font-medium">
          📍 Nearby places
        </p>
        {data.query && (
          <p className="text-muted-foreground text-xs">
            {data.query}
          </p>
        )}
      </div>

      {/* Scrollable card row */}
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        role="list"
        aria-label="List of nearby places"
      >
        {sortedPlaces.map((place, idx) => (
          <div key={`${place.name}-${idx}`} role="listitem">
            <PlaceCard
              place={place}
              userLocation={data.location}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
